import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { initiativesApi, reportsApi, metricsApi } from '../services/trackerApi';
import { useToast } from '../hooks/useToast';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { DateField } from '../components/DateField';
import { RichTextField, isRichTextEmpty } from '../components/RichTextField';
import { plainTextToHtml } from '../components/RichTextView';
import { rollupProgress, rollupHealth, buildCensus, toDraftChangeLists, formatMilestoneChange } from '../utils/reportRollup';
import type { Initiative, WeeklyReport } from '../types/trackerModels';
import './SubmitReportPage.css';

const weekOf = () => {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().slice(0, 10);
};

type Commitment = { kind: 'initiative'; id: string; label: string };
type Health = 'On Track' | 'At Risk' | 'Blocked';

const HEALTH_VALUES: Health[] = ['On Track', 'At Risk', 'Blocked'];
const HEALTH_TONE: Record<Health, 'success' | 'warning' | 'danger'> = {
  'On Track': 'success',
  'At Risk': 'warning',
  Blocked: 'danger',
};
const HEALTH_ICON: Record<Health, string> = {
  'On Track': 'ri-checkbox-circle-line',
  'At Risk': 'ri-alert-line',
  Blocked: 'ri-close-circle-line',
};

const STEPS = [
  { icon: 'ri-calendar-line', label: 'This week' },
  { icon: 'ri-flag-line', label: 'Progress' },
  { icon: 'ri-git-branch-line', label: 'What changed' },
  { icon: 'ri-edit-2-line', label: 'Update' },
  { icon: 'ri-shield-check-line', label: 'Schedule' },
];

const RING_R = 52;
const RING_CIRC = 2 * Math.PI * RING_R;

/** Programme/Portfolio weekly report - same draft/review/submit ritual as a Project's
 *  (SubmitReportPage.tsx), but every "what changed" signal is rolled up from this initiative's
 *  direct children's own already-filed reports, one hop, rather than read from fields this
 *  initiative doesn't own directly. See utils/reportRollup.ts for the rollup math itself. */
export const SubmitRollupReportPage: React.FC<{ initiative: Initiative }> = ({ initiative }) => {
  const { id, reportId } = useParams<{ id: string; reportId?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { personId } = useCurrentUser();

  // isSuccess (not just the []-defaulted data) gates the prefill effect below - this query is
  // almost always already warm from InitiativeDetailPage.tsx's own identical queryKey (the
  // page this route is always navigated from), but a direct/deep link shouldn't be allowed to
  // silently prefill percentComplete/health from an empty children array before this resolves.
  const { data: allInitiatives = [], isSuccess: initiativesLoaded } = useQuery<Initiative[]>({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  const children = allInitiatives.filter((i) => i.parentInitiativeId === initiative.initiativeId);

  const { data: reports = [] } = useQuery<WeeklyReport[]>({
    queryKey: ['reports', id],
    queryFn: () => reportsApi.list(id!),
    enabled: !!id,
  });
  const { data: allMetrics = [] } = useQuery({ queryKey: ['metrics'], queryFn: metricsApi.getAll });
  const targetMetrics = allMetrics.filter((m: any) => initiative.targetMetricIds?.includes(m.metricId));

  // Each direct child's own two most recent filed reports - the one-hop cascade this whole
  // page is built from. Same fan-out shape InitiativeDetailPage.tsx already runs for the
  // %-delta chip; here both the latest AND previous report are kept (not just the delta)
  // since buildCensus() needs both to diff against.
  const childReportQueries = useQueries({
    queries: children.map((c) => ({
      queryKey: ['reports', c.initiativeId],
      queryFn: () => reportsApi.list(c.initiativeId),
      enabled: !!c.initiativeId,
    })),
  });
  const latestReportByChild: Record<string, WeeklyReport | undefined> = {};
  const previousReportByChild: Record<string, WeeklyReport | undefined> = {};
  children.forEach((c, i) => {
    const sorted = ((childReportQueries[i]?.data as WeeklyReport[]) || []).slice().sort((a, b) => b.weekOf.localeCompare(a.weekOf));
    latestReportByChild[c.initiativeId] = sorted[0];
    previousReportByChild[c.initiativeId] = sorted[1];
  });
  const census = buildCensus(children, latestReportByChild, previousReportByChild);

  const [selectedWeek, setSelectedWeek] = useState(weekOf());
  const existingReport = reportId
    ? reports.find((r) => r.reportId === reportId)
    : reports.find((r) => r.weekOf === selectedWeek);
  const isEditing = !!existingReport;
  const previousReport = reports
    .filter((r) => r.reportId !== existingReport?.reportId)
    .filter((r) => r.weekOf < (existingReport?.weekOf || selectedWeek))
    .sort((a, b) => b.weekOf.localeCompare(a.weekOf))[0];

  const [percentComplete, setPercentComplete] = useState(0);
  const [health, setHealth] = useState<Health>('On Track');
  const [committedNext, setCommittedNext] = useState<Commitment[]>([]);
  const [metricReadingInputs, setMetricReadingInputs] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [blockers, setBlockers] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [projectedEndDate, setProjectedEndDate] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [riskCreatedFromBlockers, setRiskCreatedFromBlockers] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRefs = useRef<(HTMLElement | null)[]>([null, null, null, null, null]);

  useEffect(() => {
    if (loaded) return;
    if (existingReport) {
      setSelectedWeek(existingReport.weekOf);
      setPercentComplete(existingReport.percentComplete);
      setHealth(existingReport.health as any);
      setSummary(existingReport.summary);
      setBlockers(existingReport.blockers);
      setNextSteps(existingReport.nextSteps);
      setProjectedEndDate(existingReport.projectedEndDate);
      setCommittedNext((existingReport.committedNext || []) as Commitment[]);
      setLoaded(true);
    } else if (initiativesLoaded) {
      setPercentComplete(rollupProgress(children) ?? 0);
      const rolled = rollupHealth(children);
      setHealth(rolled === 'Not Started' ? 'On Track' : rolled);
      setCommittedNext([]);
      setProjectedEndDate(initiative.currentProjectedEndDate);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingReport, initiative, initiativesLoaded, loaded]);

  useEffect(() => {
    if (!loaded) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target as HTMLElement);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-10% 0px -55% 0px' }
    );
    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [loaded]);

  const toggleCommitment = (c: Commitment) => {
    setCommittedNext((prev) => (prev.some((x) => x.id === c.id) ? prev.filter((x) => x.id !== c.id) : [...prev, c]));
  };
  const openCommitmentOptions: Commitment[] = children
    .filter((c) => c.status !== 'Completed' && c.status !== 'Cancelled')
    .map((c) => ({ kind: 'initiative', id: c.initiativeId, label: c.name }));
  const isCommitmentDone = (c: Commitment) => children.find((child) => child.initiativeId === c.id)?.status === 'Completed';

  const openRisks = (initiative.risks || []).filter((r) => r.status === 'Open');

  const ringOffset = RING_CIRC - (RING_CIRC * Math.min(100, Math.max(0, percentComplete))) / 100;
  const weekDelta = previousReport ? percentComplete - previousReport.percentComplete : null;

  const handleWeekChange = (newWeek: string) => {
    setSelectedWeek(newWeek);
    setLoaded(false);
  };
  const scrollToStep = (idx: number) => {
    sectionRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pendingBlocked = !!initiative.pendingProjectedEndDate;
  const dateChanged = !!initiative.currentProjectedEndDate && !pendingBlocked && projectedEndDate !== initiative.currentProjectedEndDate;
  const summaryEmpty = isRichTextEmpty(summary);
  const needsExceptionReason = dateChanged && !exceptionReason.trim();
  const canSubmit = !summaryEmpty && !needsExceptionReason;

  const metricReadingsPayload = () =>
    Object.entries(metricReadingInputs)
      .filter(([, v]) => v.trim() !== '')
      .map(([metricId, v]) => ({ metricId, value: Number(v) }));

  const submitMutation = useMutation({
    mutationFn: () => {
      const data: any = {
        health, summary, blockers, nextSteps, projectedEndDate,
        percentComplete,
        // This initiative doesn't own milestones/requirements directly - what changed below
        // (census) is read-only reference material sourced from children's own reports, not
        // re-recorded here.
        milestoneUpdates: [],
        requirementUpdates: [],
        committedNext,
        metricReadings: metricReadingsPayload(),
      };
      if (dateChanged) data.exceptionReason = exceptionReason;
      return existingReport
        ? reportsApi.update(id!, existingReport.reportId, data)
        : reportsApi.create(id!, { ...data, weekOf: selectedWeek, submittedBy: personId });
    },
    onSuccess: () => {
      toast.success(existingReport ? 'Report updated.' : 'Report submitted.');
      queryClient.invalidateQueries({ queryKey: ['reports', id] });
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      navigate(`/tracker/initiatives/${id}`);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save report'),
  });

  const createRiskFromBlockers = useMutation({
    mutationFn: () => {
      const plainText = blockers.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return initiativesApi.update(id!, {
        risks: [...(initiative.risks || []), { id: `RSK-${Date.now()}`, description: plainText, severity: 'Medium', status: 'Open' }],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setRiskCreatedFromBlockers(true);
      toast.success('Risk created from blockers text.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to create risk'),
  });

  // Same reportsApi.draft() route the Project form uses - draft_report on the backend consumes
  // generic already-flattened change lists, so feeding it this rollup's census works unchanged.
  const draftMutation = useMutation({
    mutationFn: () => {
      const { milestoneChanges, requirementChanges, lateMilestones } = toDraftChangeLists(census);
      const metricReadings = metricReadingsPayload().map((mr) => {
        const metric = targetMetrics.find((m: any) => m.metricId === mr.metricId);
        return {
          name: metric?.name || '', unit: metric?.unit || '', value: mr.value,
          onTarget: metric?.target == null ? true : (metric.direction === 'lower' ? mr.value <= metric.target : mr.value >= metric.target),
        };
      });
      return reportsApi.draft(id!, {
        milestoneChanges, requirementChanges, lateMilestones, metricReadings,
        previousNextSteps: previousReport ? previousReport.nextSteps.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '',
        health,
      });
    },
    onSuccess: (draft) => {
      setSummary(plainTextToHtml(draft.summary || ''));
      setBlockers(plainTextToHtml(draft.blockers || ''));
      setNextSteps(plainTextToHtml(draft.nextSteps || ''));
      toast.success('Draft generated — review and edit before submitting.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to generate a draft'),
  });

  if (!loaded) return <p className="text-muted container-xxl flex-grow-1 container-p-y">Loading…</p>;

  const childLabel = initiative.tier === 'Portfolio' ? 'programmes' : 'projects';

  return (
    <div className="container-xxl flex-grow-1 container-p-y rf">
      <div className="rf-container">
      <div className="rf-crumbs">
        <span>Initiative Tracker</span>
        <span className="sep">›</span>
        <span className="current">{initiative.name}</span>
      </div>

      <div className="rf-head">
        <div>
          <h1>{isEditing ? 'Edit Weekly Report' : 'Submit Weekly Report'}</h1>
          <p className="rf-sub">
            Reporting week <strong className="rf-tabular">{formatWeek(reportId ? (existingReport?.weekOf || selectedWeek) : selectedWeek)}</strong>
          </p>
        </div>
        <span className="d-flex align-items-center gap-2">
          {!isEditing && (
            <span className="rf-chip rf-tone-warning"><i className="ri-draft-line"></i>Draft</span>
          )}
          <span className={`rf-chip rf-tone-${HEALTH_TONE[health]}`}>
            <i className={HEALTH_ICON[health]}></i>
            {health}
          </span>
        </span>
      </div>

      {isEditing && (
        <div className="np-banner np-tone-info mb-3">
          <i className="ri-information-line"></i>
          <span>{reportId ? 'Editing a past report.' : "A report already exists for this week — editing it instead of creating a duplicate."}</span>
        </div>
      )}

      <div className="rf-layout">
        <aside className="rf-rail">
          <div className="rf-ring-wrap">
            <svg className="rf-ring" viewBox="0 0 120 120">
              <circle className="rf-ring-track" cx="60" cy="60" r={RING_R} />
              <circle className="rf-ring-fill" cx="60" cy="60" r={RING_R} strokeDasharray={RING_CIRC} strokeDashoffset={ringOffset} />
            </svg>
            <div className="rf-ring-label">
              <span className="rf-ring-pct rf-tabular">{percentComplete}%</span>
              <span className="rf-ring-caption">Overall</span>
            </div>
          </div>
          {weekDelta !== null && (
            <span className={`rf-delta-badge ${weekDelta >= 0 ? 'up' : 'down'}`}>
              <i className={weekDelta >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
              {Math.abs(weekDelta)}% vs last week
            </span>
          )}

          <nav className="rf-steps" style={{ ['--rf-fill' as any]: `${(activeStep / (STEPS.length - 1)) * 100}%` }}>
            {STEPS.map((step, i) => (
              <button
                key={step.label}
                type="button"
                className={`rf-step ${activeStep === i ? 'active' : ''}`}
                onClick={() => scrollToStep(i)}
              >
                <span className="rf-dot"><i className={step.icon}></i></span>
                <span>
                  <span className="rf-index">0{i + 1}</span>
                  <span className="rf-name">{step.label}</span>
                </span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="rf-content">
          <section id="s1" className="rf-block" ref={(el) => { sectionRefs.current[0] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-calendar-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 01</span>
                  <h2>This week</h2>
                </div>
              </div>
            </div>

            <div className="rf-row2">
              <div className="rf-field">
                <label className="rf-field-label">Reporting week</label>
                {reportId ? (
                  <div className="rf-date">
                    <input className="form-control" value={formatWeek(existingReport?.weekOf || selectedWeek)} readOnly />
                  </div>
                ) : (
                  <>
                    <div className="rf-date">
                      <DateField className="form-control" value={selectedWeek} onChange={handleWeekChange} />
                    </div>
                    <p className="rf-field-hint">Defaults to this week — change it to file/test a report against a different week.</p>
                  </>
                )}
              </div>

              <div className="rf-field">
                <label className="rf-field-label">Health this week</label>
                <div className="rf-health-tiles">
                  {HEALTH_VALUES.map((h) => (
                    <button
                      key={h}
                      type="button"
                      className={`rf-htile ${health === h ? 'active' : ''}`}
                      data-tone={HEALTH_TONE[h]}
                      onClick={() => setHealth(h)}
                    >
                      <span className="rf-hicon"><i className={HEALTH_ICON[h]}></i></span>
                      <span className="rf-hlabel">{h}</span>
                    </button>
                  ))}
                </div>
                <p className="rf-field-hint">Pre-filled from {children.length} {childLabel}' health this week — override if it doesn't tell the real story.</p>
              </div>
            </div>
          </section>

          <section id="s2" className="rf-block" ref={(el) => { sectionRefs.current[1] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-flag-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 02</span>
                  <h2>Progress</h2>
                </div>
              </div>
            </div>

            <div className="rf-field">
              <div className="rf-m-track">
                <input
                  type="range"
                  min={0}
                  max={100}
                  className="rf-slider"
                  value={percentComplete}
                  style={{ ['--rf-pct' as any]: `${percentComplete}%` }}
                  onChange={(e) => setPercentComplete(Number(e.target.value))}
                />
                <span className="rf-m-pct rf-tabular">{percentComplete}%</span>
              </div>
              <p className="rf-field-hint">Averaged across {children.length} {childLabel}' own percentComplete — override if it doesn't tell the real story.</p>
            </div>

            {targetMetrics.length > 0 && (
              <div className="rf-field" style={{ marginTop: 24 }}>
                <label className="rf-field-label">This week's metrics</label>
                {targetMetrics.map((m: any) => (
                  <div key={m.metricId} className="rf-metric-row">
                    <span className="rf-metric-name">{m.name}</span>
                    <input
                      className="form-control"
                      style={{ maxWidth: 140 }}
                      placeholder={m.unit || 'Value'}
                      value={metricReadingInputs[m.metricId] || ''}
                      onChange={(e) => setMetricReadingInputs((prev) => ({ ...prev, [m.metricId]: e.target.value }))}
                    />
                    {m.unit && <span className="rf-metric-unit">{m.unit}</span>}
                  </div>
                ))}
                <p className="rf-field-hint">Only readings you enter here are logged — leave any blank to skip it this week.</p>
              </div>
            )}
          </section>

          <section id="s3" className="rf-block" ref={(el) => { sectionRefs.current[2] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-git-branch-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 03</span>
                  <h2>What changed below</h2>
                </div>
              </div>
            </div>
            <p className="rf-field-hint" style={{ marginBottom: 16 }}>
              From {children.length} {childLabel}' own reports this week — read-only, edit these on each {initiative.tier === 'Portfolio' ? 'programme' : 'project'} directly.
            </p>

            {census.totalChanges === 0 && (
              <p className="rf-field-hint">Nothing has changed below since last report.</p>
            )}

            {census.milestoneChanges.length > 0 && (
              <div className="rf-field">
                <label className="rf-field-label">Milestones ({census.milestoneChanges.length})</label>
                {census.milestoneChanges.map((c) => (
                  <div key={`${c.childId}-${c.milestoneId}`} className="rf-requirement">
                    <span className="rf-req-text">
                      {c.late ? <span className="rf-chip rf-tone-danger" style={{ marginRight: 6 }}>Target date passed</span> : null}
                      {c.name} <span className="text-muted">— {c.childName}</span>
                    </span>
                    {!c.late && <span className="np-sub small">{formatMilestoneChange(c)}</span>}
                  </div>
                ))}
              </div>
            )}

            {census.requirementChanges.length > 0 && (
              <div className="rf-field">
                <label className="rf-field-label">Requirements ({census.requirementChanges.length})</label>
                {census.requirementChanges.map((c) => (
                  <div key={`${c.childId}-${c.requirementId}`} className="rf-requirement">
                    <span className="rf-req-text">{c.text} <span className="text-muted">— {c.childName}</span></span>
                    <span className="np-sub small">{c.from} → {c.to}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section id="s4" className="rf-block" ref={(el) => { sectionRefs.current[3] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-edit-2-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 04</span>
                  <h2>Update</h2>
                </div>
              </div>
              <button
                type="button"
                className="np-btn np-btn-sm np-btn-ghost"
                disabled={draftMutation.isPending}
                onClick={() => draftMutation.mutate()}
              >
                {draftMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : <i className="ri-magic-line"></i>}
                Generate draft
              </button>
            </div>

            <div className="rf-field">
              <label className="rf-field-label">Summary</label>
              <RichTextField value={summary} onChange={setSummary} placeholder="What happened this week…" height={120} />
            </div>

            <div className="rf-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label className="rf-field-label" style={{ marginBottom: 0 }}>Blockers</label>
                {!isRichTextEmpty(blockers) && !riskCreatedFromBlockers && (
                  <button
                    type="button"
                    className="rf-risk-chip"
                    disabled={createRiskFromBlockers.isPending}
                    onClick={() => createRiskFromBlockers.mutate()}
                  >
                    <i className="ri-shield-flash-line"></i>Turn into tracked risk
                  </button>
                )}
                {riskCreatedFromBlockers && (
                  <span className="rf-risk-done"><i className="ri-check-line"></i>Risk created</span>
                )}
              </div>
              {openRisks.length > 0 && (
                <div className="rf-open-risks">
                  <span className="rf-field-hint" style={{ margin: '0 8px 0 0' }}>Still blocking?</span>
                  {openRisks.map((r) => (
                    <span key={r.id} className="rf-chip rf-tone-warning" title={r.description}>{r.description}</span>
                  ))}
                </div>
              )}
              <RichTextField
                value={blockers}
                onChange={(html) => { setBlockers(html); setRiskCreatedFromBlockers(false); }}
                placeholder="Anything blocking progress…"
                height={90}
              />
            </div>

            <div className="rf-field">
              <label className="rf-field-label">Next steps</label>
              <RichTextField value={nextSteps} onChange={setNextSteps} placeholder="Plans for next week…" height={90} />
            </div>

            <div className="rf-field">
              <label className="rf-field-label">Committing to next week</label>
              {!!previousReport?.committedNext?.length && (
                <div className="rf-commit-check-list">
                  <p className="rf-field-hint" style={{ marginTop: 0 }}>You committed to these last week:</p>
                  {previousReport.committedNext.map((c) => {
                    const done = isCommitmentDone(c as Commitment);
                    return (
                      <span key={c.id} className={`rf-chip ${done ? 'rf-tone-success' : 'rf-tone-warning'}`}>
                        <i className={done ? 'ri-check-line' : 'ri-time-line'}></i>{c.label}
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="rf-field-hint">Tag which {childLabel.slice(0, -1)}(s) you expect to move next week:</p>
              <div className="rf-commit-picker">
                {openCommitmentOptions.length === 0 && <span className="rf-field-hint">Nothing open to commit to.</span>}
                {openCommitmentOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`rf-chip rf-chip-toggle ${committedNext.some((c) => c.id === opt.id) ? 'active' : ''}`}
                    onClick={() => toggleCommitment(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section id="s5" className="rf-block" ref={(el) => { sectionRefs.current[4] = el; }}>
            <div className="rf-block-head">
              <div className="rf-title-group">
                <span className="rf-icon-badge"><i className="ri-shield-check-line"></i></span>
                <div>
                  <span className="rf-eyebrow">STEP 05</span>
                  <h2>Schedule</h2>
                </div>
              </div>
            </div>

            <div className="rf-field">
              <div className="rf-schedule-row">
                <div>
                  <label className="rf-field-label">Updated projected end date</label>
                  {pendingBlocked ? (
                    <div className="rf-date">
                      <input className="form-control" value={initiative.currentProjectedEndDate} readOnly disabled />
                    </div>
                  ) : (
                    <div className="rf-date">
                      <DateField className="form-control" value={projectedEndDate} onChange={setProjectedEndDate} />
                    </div>
                  )}
                </div>
              </div>

              {pendingBlocked && (
                <div className="rf-pending-banner">
                  <i className="ri-time-line"></i>
                  A change to {initiative.pendingProjectedEndDate} is already pending approval — resolve that exception before requesting another change.
                </div>
              )}

              {dateChanged && (
                <div className="rf-exception-panel">
                  <div className="rf-ep-head">
                    <i className="ri-alert-line"></i>
                    Reason for schedule change — required
                  </div>
                  <p>Changing the projected end date after it's been set requires an exception. It won't take effect until an approver signs off, on the initiative's Exceptions tab.</p>
                  <textarea
                    rows={2}
                    placeholder="Why is the projected end date changing?"
                    value={exceptionReason}
                    onChange={(e) => setExceptionReason(e.target.value)}
                  />
                </div>
              )}

              {!pendingBlocked && !dateChanged && (
                <p className="rf-field-hint">Unchanged this week — no sign-off required.</p>
              )}
            </div>
          </section>
        </main>
      </div>
      </div>

      <footer className="rf-action-bar">
        <div className="rf-action-inner">
          <span className={`rf-status-msg ${canSubmit ? 'ready' : 'blocked'}`}>
            <i className={canSubmit ? 'ri-checkbox-circle-line' : 'ri-information-line'}></i>
            {summaryEmpty ? 'Summary required' : needsExceptionReason ? 'Reason for schedule change required' : 'Ready to submit'}
          </span>
          <div className="rf-actions">
            <button className="np-btn np-btn-ghost" onClick={() => navigate(`/tracker/initiatives/${id}`)}>Cancel</button>
            <button
              className="np-btn np-btn-primary"
              disabled={submitMutation.isPending || !canSubmit}
              onClick={() => submitMutation.mutate()}
            >
              {submitMutation.isPending ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <i className={isEditing ? 'ri-save-line' : 'ri-send-plane-line'}></i>
              )}
              {isEditing ? 'Save changes' : 'Submit report'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

function formatWeek(iso: string) {
  const start = new Date(iso);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}
